/**
 * FileUploader Component
 *
 * Drag-and-drop file upload with progress indicator.
 * Uses GCS signed URLs for direct upload.
 */

import { type Component, createSignal, Show, For } from 'solid-js';
import {
  uploadFile,
  fetchAllowedFileTypes,
  type FileContext,
  type FileInfo,
} from '../../services/api';

interface FileUploaderProps {
  context: FileContext;
  contextId: string;
  onUploadComplete?: (file: FileInfo) => void;
  onError?: (error: string) => void;
  accept?: string; // Optional MIME type filter
  multiple?: boolean;
  maxFiles?: number;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
}

export const FileUploader: Component<FileUploaderProps> = (props) => {
  const [isDragging, setIsDragging] = createSignal(false);
  const [uploadingFiles, setUploadingFiles] = createSignal<UploadingFile[]>([]);
  const [allowedTypes, setAllowedTypes] = createSignal<string[]>([]);
  const [maxFileSize, setMaxFileSize] = createSignal(25 * 1024 * 1024); // Default 25MB
  let fileInputRef: HTMLInputElement | undefined;

  // Load allowed file types on mount
  fetchAllowedFileTypes().then((types) => {
    setAllowedTypes(types.allowedMimeTypes);
    setMaxFileSize(types.maxFileSize);
  }).catch(() => {
    // Use defaults if API fails
  });

  const validateFile = (file: File): string | null => {
    const types = allowedTypes();
    if (types.length > 0 && !types.includes(file.type)) {
      return `File type "${file.type}" is not allowed`;
    }
    if (file.size > maxFileSize()) {
      return `File size exceeds ${Math.round(maxFileSize() / 1024 / 1024)}MB limit`;
    }
    return null;
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const maxFiles = props.maxFiles ?? 10;

    // Limit number of files
    const filesToUpload = fileArray.slice(0, maxFiles);

    for (const file of filesToUpload) {
      const validationError = validateFile(file);
      if (validationError) {
        props.onError?.(validationError);
        continue;
      }

      // Add to uploading list
      const uploadEntry: UploadingFile = {
        file,
        progress: 0,
        status: 'uploading',
      };
      setUploadingFiles((prev) => [...prev, uploadEntry]);

      try {
        const uploadedFile = await uploadFile(file, props.context, props.contextId, {
          onProgress: (progress) => {
            setUploadingFiles((prev) =>
              prev.map((uf) =>
                uf.file === file ? { ...uf, progress } : uf
              )
            );
          },
        });

        // Update status to complete
        setUploadingFiles((prev) =>
          prev.map((uf) =>
            uf.file === file ? { ...uf, status: 'complete', progress: 100 } : uf
          )
        );

        props.onUploadComplete?.(uploadedFile);

        // Remove completed file after delay
        setTimeout(() => {
          setUploadingFiles((prev) => prev.filter((uf) => uf.file !== file));
        }, 2000);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        setUploadingFiles((prev) =>
          prev.map((uf) =>
            uf.file === file ? { ...uf, status: 'error', error: errorMessage } : uf
          )
        );
        props.onError?.(errorMessage);
      }
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer?.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
    fileInputRef?.click();
  };

  const handleInputChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (input.files) {
      handleFiles(input.files);
      input.value = ''; // Reset for re-upload of same file
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z';
    if (mimeType.includes('pdf')) return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z';
    if (mimeType.startsWith('text/') || mimeType.includes('json') || mimeType.includes('javascript')) return 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4';
    return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
  };

  return (
    <div class="space-y-4">
      {/* Drop Zone */}
      <div
        class={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
          ${isDragging()
            ? 'border-purple-500 bg-purple-500/10'
            : 'border-gray-700 hover:border-gray-600 bg-gray-800/50 hover:bg-gray-800'
          }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          class="hidden"
          multiple={props.multiple ?? true}
          accept={props.accept}
          onChange={handleInputChange}
        />

        <div class="flex flex-col items-center">
          <div class={`w-12 h-12 rounded-full flex items-center justify-center mb-4 transition-colors
            ${isDragging() ? 'bg-purple-500/20' : 'bg-gray-700'}`}>
            <svg class={`w-6 h-6 ${isDragging() ? 'text-purple-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>

          <p class="text-gray-300 font-medium mb-1">
            {isDragging() ? 'Drop files here' : 'Drag & drop files here'}
          </p>
          <p class="text-gray-500 text-sm">
            or click to browse
          </p>
          <p class="text-gray-600 text-xs mt-2">
            Max {Math.round(maxFileSize() / 1024 / 1024)}MB per file
          </p>
        </div>
      </div>

      {/* Upload Progress */}
      <Show when={uploadingFiles().length > 0}>
        <div class="space-y-2">
          <For each={uploadingFiles()}>
            {(uf) => (
              <div class={`flex items-center gap-3 p-3 rounded-lg
                ${uf.status === 'error' ? 'bg-red-900/20' : 'bg-gray-800'}`}>
                <div class="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={getFileIcon(uf.file.type)} />
                  </svg>
                </div>

                <div class="flex-1 min-w-0">
                  <p class="text-sm text-gray-300 truncate">{uf.file.name}</p>
                  <p class="text-xs text-gray-500">{formatFileSize(uf.file.size)}</p>

                  <Show when={uf.status === 'uploading'}>
                    <div class="mt-1.5 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        class="h-full bg-purple-500 transition-all duration-300"
                        style={{ width: `${uf.progress}%` }}
                      />
                    </div>
                  </Show>

                  <Show when={uf.status === 'error'}>
                    <p class="text-xs text-red-400 mt-1">{uf.error}</p>
                  </Show>
                </div>

                <div class="flex-shrink-0">
                  <Show when={uf.status === 'uploading'}>
                    <span class="text-xs text-gray-400">{Math.round(uf.progress)}%</span>
                  </Show>
                  <Show when={uf.status === 'complete'}>
                    <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </Show>
                  <Show when={uf.status === 'error'}>
                    <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};
